import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";

type MaxWidth = "sm" | "md" | "lg" | "xl" | "2xl" | "full";
type Padding = "none" | "sm" | "md" | "lg";

interface PageLayoutProps {
  children: ReactNode;
  maxWidth?: MaxWidth;
  padding?: Padding;
  className?: string;
}

const maxWidthClasses: Record<MaxWidth, string> = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-5xl",
  xl: "max-w-6xl",
  "2xl": "max-w-7xl",
  full: "max-w-full",
};

const paddingClasses: Record<Padding, string> = {
  none: "p-0",
  sm: "p-2 md:p-4",
  md: "p-0 sm:p-4 md:p-6",
  lg: "p-6 md:p-8",
};

/**
 * PageLayout - Standard page wrapper for consistent layout across the app
 * 
 * Provides:
 * - Responsive padding that adapts to screen size
 * - Configurable max-width for content containment
 * - Consistent centering and spacing
 * 
 * @example
 * <PageLayout maxWidth="xl" padding="md">
 *   <PageHeader title="Estimates" />
 *   <EstimatesList />
 * </PageLayout>
 */
const PageLayout = ({
  children,
  maxWidth = "xl",
  padding = "md",
  className,
}: PageLayoutProps) => {
  return (
    <div
      className={cn(
        "w-full mx-auto min-w-0",
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
};

export default PageLayout;
